import { FC, useEffect, useState } from 'react';
import { Button, TableCell, TableRow } from '@mui/material';
import { LaunchpadTable } from '../../components/mui-overrides/LaunchpadTable';
import { useTransactions } from '../../hooks/useTransactions';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useAssets } from 'src/hooks/useAssets';
import { Asset } from 'src/models';

interface Props {
  status: 'reserved' | 'success';
  reload: boolean;
  openNotaryModal: (transactionId: string) => void;
  openPaymentModal: (transactionId: string) => void;
  openSerialNumberModal: (props: { transactionId: string; assetId: string }) => void;
}

const initialPage = 0;
const initialRowsPerPage = 25;

export const TransactionsTable: FC<Props> = ({ status, reload, openNotaryModal, openPaymentModal, openSerialNumberModal }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const transactionsData = useTransactions({ status, page, rowsPerPage }, reload);
  const transactions = transactionsData?.data ?? [];
  const transactionCount = transactionsData?.count ?? 0;
  // if there are transactions we want to extract assetIds
  const assets = useAssets(false);
  // map assetIds to assets
  const assetMap = new Map<string, Asset>();
  assets.primaryAssets?.forEach((asset) => {
    assetMap.set(asset.id, asset);
  });
  assets.secondaryAssets?.forEach((asset) => {
    assetMap.set(asset.id, asset);
  });
  const showNotary = transactions.some((transaction) => assetMap.get(transaction.assetId)?.type === 'builderOwnerModel');

  useEffect(() => {
    setPage(initialPage);
  }, [reload]);

  return (
    <LaunchpadTable
      hidePagination={false}
      page={page}
      rowsPerPage={rowsPerPage}
      setPage={setPage}
      setRowsPerPage={(rowsPerPage) => {
        setPage(initialPage);
        setRowsPerPage(rowsPerPage);
      }}
      length={transactionCount}
      renderHeader={() => (
        <TableRow>
          <TableCell>{t('transactions.overview.table.userId')}</TableCell>
          <TableCell>{t('transactions.overview.table.createdAt')}</TableCell>
          <TableCell>{t('transactions.overview.table.amount')}</TableCell>
          <TableCell>{t('transactions.overview.table.asset')}</TableCell>
          <TableCell>{t('transactions.overview.table.name')}</TableCell>
          <TableCell>{t('transactions.overview.table.email')}</TableCell>
          {status === 'reserved' && <TableCell>{t('transactions.overview.table.payment')}</TableCell>}
          {showNotary && (
            <TableCell>
              {status === 'reserved' ? t('transactions.overview.table.notary') : t('transactions.overview.table.notaryFile')}
            </TableCell>
          )}
          <TableCell>{t('dashboard.assets.addedit.link.serialNumber')}</TableCell>
        </TableRow>
      )}
      renderRows={() => (
        <>
          {transactions.map((row, index) => (
            <TableRow key={index} hover style={{ cursor: 'pointer', textDecoration: 'none' }}>
              <TableCell>{row.userId}</TableCell>
              <TableCell>{format(new Date(row.createdAt), 'dd.MM.yyyy, HH:mm')}</TableCell>
              <TableCell>{row.amount}</TableCell>
              <TableCell>{row.assetTitle}</TableCell>
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.email}</TableCell>
              {status === 'reserved' && (
                <TableCell style={{ textAlign: 'center' }}>
                  <Button
                    onClick={() => {
                      openPaymentModal(row.paymentTransactionId);
                    }}
                    style={{
                      textTransform: 'none',
                      color: 'black',
                      fontWeight: '400',
                      border: 'solid',
                      borderColor: 'grey',
                    }}
                  >
                    {t('transactions.overview.table.paymentOpen')}
                  </Button>
                </TableCell>
              )}
              {showNotary && (
                <TableCell style={{ textAlign: 'center' }}>
                  {assetMap.get(row.assetId)?.type === 'builderOwnerModel' && (
                    <>
                      {status === 'reserved' ? (
                        <Button
                          onClick={() => openNotaryModal(row.id)}
                          style={{
                            textTransform: 'none',
                            color: 'black',
                            border: 'solid',
                            borderColor: 'grey',
                            fontWeight: '400',
                          }}
                        >
                          {t('transactions.overview.table.notaryOpen')}
                        </Button>
                      ) : row.notaryDocumentURL ? (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            window.open(row.notaryDocumentURL, '_blank');
                          }}
                        >
                          {t('transactions.overview.table.downloadFile')}
                        </Button>
                      ) : (
                        t('transactions.overview.table.fileNotAvailable')
                      )}
                    </>
                  )}
                </TableCell>
              )}

              <TableCell>
                {(assetMap.get(row.assetId) || {}).useSerialNumber || true ? (
                  <Button
                    onClick={() => {
                      console.log('row', row);
                      openSerialNumberModal({ transactionId: row.id, assetId: row.assetId });
                    }}
                    style={{
                      textTransform: 'none',
                      color: 'black',
                      fontWeight: '400',
                      border: 'solid',
                      borderColor: 'grey',
                    }}
                  >
                    {t('transactions.overview.table.showSerialNumber')}
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </>
      )}
    />
  );
};
